import {
  Store,
  select
} from '@ngrx/store';
import { EXPANDED_ROW_CONTEXT } from '../tokens';
import {
  Inject,
  Optional,
  OnInit,
  OnDestroy,
  Input,
  Injector
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogRef
} from '@angular/material';
import {
  Observable,
  Subscription,
  of
} from 'rxjs';
import { getDocumentById } from '../documents/document.selectors';
import {
  filter,
  map,
  distinctUntilChanged,
  startWith
} from 'rxjs/operators';
import {
  isDialogContextWithTableId,
  isDialogContextWithDocumentId,
  isDialogContextWithDocument
} from './dialog.actions';
import { ClearExpandedRowId } from '../tables/table.actions';
import {
  getRowId
} from '../tables/utilities';
import { ActivatedRoute } from '@angular/router';
import { IAbstractViewComponent } from './abstract-view.component.interface';
import { NGXLogger } from 'ngx-logger';
import { WINDOW_CONTEXT } from '../windows/tokens';
import { IWithIdentifier } from '@digitaix/types';
import { WindowContext } from '../windows/window-context';
import {
  CloseWindow,
  SetWindowTitle
} from '../windows/window.actions';

export class AbstractViewComponent<
  D,
  AppState = any,
  C = any,
  R extends IWithIdentifier = IWithIdentifier
> implements OnInit, OnDestroy, IAbstractViewComponent<D> {
  public document$!: Observable<D>;

  @Input() public document: D | null = null;
  public documentId: string;
  public tableId: string | null;
  public formId: string | null;
  public collectionPath: string | null;

  public readonly windowContext: WindowContext<D> | null = null;

  protected _subscription = new Subscription();

  constructor(
    public readonly store$: Store<AppState>,
    @Optional()
    @Inject(EXPANDED_ROW_CONTEXT)
    public readonly context: any | null,
    @Optional()
    @Inject(MAT_DIALOG_DATA)
    public readonly data: any | null,
    @Optional() public readonly dialogRef: MatDialogRef<C>,
    public route: ActivatedRoute,
    public logger: NGXLogger,
    @Optional()
    @Inject(WINDOW_CONTEXT)
    windowContext: any | null,
    public injector: Injector
  ) {
    const _data: any = data;
    this.windowContext = windowContext;
    Object.assign(this, this.route.snapshot.data || {});
    this.collectionPath =
      (context && context.collectionPath) ||
      (_data && _data.collectionPath) ||
      route.snapshot.data.collectionPath ||
      (context && context.row && context.row.collectionPath) ||
      null;
    this.tableId =
      (context && context.tableId) ||
      (_data && _data.tableId) ||
      route.snapshot.data.tableId ||
      null;
    this.documentId =
      (context && getRowId(context.row)) ||
      (_data && (getRowId(_data.row) || _data.documentId)) ||
      route.snapshot.params.id ||
      null;
    this.formId =
      (context && context.formId) ||
      (_data && _data.formId) ||
      route.snapshot.data.formId ||
      null;
    if (windowContext) {
      this.document = windowContext.data;
    }
    this.logger.debug(
      '[AbstractViewComponent::constructor] EXPANDED_ROW_CONTEXT',
      context
    );
    this.logger.debug(
      '[AbstractViewComponent::constructor] MAT_DIALOG_DATA',
      data
    );
    this.logger.debug('[AbstractViewComponent::constructor]', {
      collectionPath: this.collectionPath,
      tableId: this.tableId,
      documentId: this.documentId,
      formId: this.formId
    });
  }

  ngOnInit(): void {
    if (this.document) {
      this.document$ = of(this.document);
    } else {
      if (this.data) {
        if (isDialogContextWithTableId<D>(this.data)) {
          this.document$ = this.store$.pipe(
            select(getDocumentById(
              this.collectionPath,
              this.documentId
            ) as any),
            filter(d => !!d)
          );
        } else if (isDialogContextWithDocumentId<D>(this.data)) {
          this.document$ = this.store$.pipe(
            select(getDocumentById(
              this.collectionPath,
              this.documentId
            ) as any),
            filter(d => !!d)
          ) as any;
        } else if (isDialogContextWithDocument<D>(this.data)) {
          this.document$ = of(this.data.document);
        } else {
          console.error(this.data);
          throw new Error('Unkown dialog context');
        }
      } else {
        this.document$ = this.store$.pipe(
          select(getDocumentById(this.collectionPath, this.documentId) as any),
          filter(d => !!d),
          startWith(this.context && this.context.row)
        );
      }
    }

    this._subscription.add(
      this.document$
        .pipe(
          filter(Boolean),
          filter(document => document.hasOwnProperty('id')),
          map((document: any) => document.id),
          distinctUntilChanged()
        )
        .subscribe(id => (this.documentId = id))
    );
    this._subscription.add(
      this.document$
        .pipe(
          filter(Boolean),
          distinctUntilChanged()
        )
        .subscribe(document => (this.document = document))
    );
    this._subscription.add(
      this.document$
        .pipe(
          filter(Boolean),
          filter(document => document.hasOwnProperty('collectionPath')),
          map((document: any) => document.collectionPath),
          distinctUntilChanged()
        )
        .subscribe(collectionPath => (this.collectionPath = collectionPath))
    );
  }

  public close(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    } else if(this.windowContext) {
      this.store$.dispatch(new CloseWindow(this.windowContext.id, 'AbstractViewComponent::close'));
    } else if (this.tableId) {
      this.store$.dispatch(new ClearExpandedRowId(this.tableId));
    }
  }

  public setTitle(title: string): void {
    if (this.windowContext) {
      this.store$.dispatch(new SetWindowTitle(this.windowContext.id, title, 'AbstractViewComponent::setTitle'));
    }
  }

  public ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }
}
